import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShoppingBag, Plane, ShieldCheck, Package, ChevronLeft, Star, MessageCircle } from "lucide-react";
import { api, resolveImg, formatPrice, getImages } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { PassportStamp } from "@/components/motifs";
import ProductCard from "@/components/ProductCard";
import ReviewsSection from "@/components/ReviewsSection";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { settings } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setActive(0);
      api.get("/products", { params: { category: data.category } }).then(({ data: rel }) => {
        setRelated(rel.filter((p) => p.id !== data.id).slice(0, 4));
      });
    });
  }, [id]);

  if (!product) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-amber-600 border-t-transparent" />
      </div>
    );
  }

  const discount =
    product.compare_at_price && product.compare_at_price > product.price
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : 0;

  const imgs = getImages(product);
  const mainImg = imgs[active] || imgs[0];

  const wa = settings?.whatsapp_number || "919999999999";
  const inquireMsg = encodeURIComponent(
    `Hi PRIYARAA ✈️ — I'm interested in "${product.name}" (${formatPrice(product.price)}). Is it available?`
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <button
        data-testid="back-button"
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-1 text-sm text-stone-500 hover:text-amber-700"
      >
        <ChevronLeft size={16} /> Back
      </button>

      <div className="grid gap-10 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <div className="relative overflow-hidden rounded-2xl border border-[#E7DFD3] bg-[#F3EDE2]">
            <img
              src={resolveImg(mainImg)}
              alt={product.name}
              data-testid="pdp-main-image"
              className="aspect-square w-full object-cover"
            />
            {product.badge && (
              <div className="absolute left-4 top-4">
                <PassportStamp label={product.badge} className="scale-110" />
              </div>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-1" data-testid="pdp-thumbnails">
              {imgs.map((im, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`pdp-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg border-2 bg-[#F3EDE2] transition-colors ${
                    active === i ? "border-amber-500" : "border-[#E7DFD3] hover:border-amber-300"
                  }`}
                >
                  <img src={resolveImg(im)} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-700">{product.category}{product.segment ? ` · ${product.segment}` : ""}</p>
          <h1 className="mt-2 font-serif text-4xl leading-tight text-stone-900">{product.name}</h1>
          <div className="mt-3 flex items-center gap-1 text-amber-500">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={15} fill="currentColor" />
            ))}
            <span className="ml-2 text-xs text-stone-500">Anti-Tarnish · Skin-Friendly</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-sans text-3xl font-semibold text-stone-900">{formatPrice(product.price)}</span>
            {discount > 0 && (
              <>
                <span className="text-lg text-stone-400 line-through">{formatPrice(product.compare_at_price)}</span>
                <span className="rounded-full bg-[#B91C1C] px-2 py-0.5 font-mono text-[10px] text-white">
                  SAVE {discount}%
                </span>
              </>
            )}
          </div>

          <p className="mt-5 leading-relaxed text-stone-600">{product.description}</p>

          <div className="mt-4 grid grid-cols-2 gap-3 font-mono text-[11px] uppercase tracking-wide text-stone-500">
            <div className="rounded-lg border border-[#E7DFD3] bg-white p-3">
              <p className="text-amber-700/70">Material</p>
              <p className="mt-1 text-stone-800">{product.material}</p>
            </div>
            <div className="rounded-lg border border-[#E7DFD3] bg-white p-3">
              <p className="text-amber-700/70">Availability</p>
              <p className="mt-1 text-stone-800">{product.stock > 0 ? "In Stock" : "Made to Order"}</p>
            </div>
          </div>

          {/* qty + add */}
          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center rounded-full border border-[#E7DFD3] bg-white">
              <button data-testid="pdp-decr" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 text-stone-700">−</button>
              <span className="w-8 text-center font-mono">{qty}</span>
              <button data-testid="pdp-incr" onClick={() => setQty((q) => q + 1)} className="px-4 py-3 text-stone-700">+</button>
            </div>
            <button
              data-testid="pdp-add-to-bag"
              onClick={() => {
                addItem(product, qty);
                toast.success(`${product.name} added to your flight bag`);
              }}
              className="flex flex-1 items-center justify-center gap-2 rounded-full gold-gradient py-3.5 font-semibold uppercase tracking-wider text-stone-900 transition-transform hover:scale-[1.02]"
            >
              <ShoppingBag size={18} /> Add to Bag
            </button>
          </div>

          <a
            data-testid="pdp-whatsapp-inquire"
            href={`https://wa.me/${wa}?text=${inquireMsg}`}
            target="_blank"
            rel="noreferrer"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#25D366] py-3 text-sm font-medium text-[#1a9e4b] hover:bg-[#25D366]/10"
          >
            <MessageCircle size={17} /> Ask on WhatsApp
          </a>

          {/* trust */}
          <div className="mt-6 grid grid-cols-3 gap-3 border-t border-[#E7DFD3] pt-6">
            {[
              [ShieldCheck, "Anti-Tarnish"],
              [Plane, "Air Insured"],
              [Package, "Passport Box"],
            ].map(([Icon, label]) => (
              <div key={label} className="flex flex-col items-center gap-1 text-center">
                <Icon size={20} className="text-amber-700" />
                <span className="text-[11px] text-stone-500">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <ReviewsSection productId={product.id} />
      </div>

      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-serif text-2xl text-stone-900">You may also love</h2>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
